import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileCheck2, ShieldCheck, Wand2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';
import { Chip } from './OpponentPanel';
import { RewriteResult } from './RewriteResult';

const VERDICT = { listo: ['Listo para presentar', 'text-emerald-300'], corregir: ['Corregir antes de presentar', 'text-amber-300'], no_presentar: ['No presentar así', 'text-red-300'] };
const STATE = { vigente: 'text-emerald-300', derogado: 'text-red-300', no_encontrado: 'text-red-300', no_verificable: 'text-slate-400' };

export const ReviewPanel = ({ matter }) => {
  const { api } = useAuth();
  const [text, setText] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixed, setFixed] = useState(null);
  const rewrite = async () => {
    setFixing(true);
    try { setFixed(await runAiJob(api, `/cerebro/matters/${matter.id}/rewrite`, { text, problems: data.problemas || [] }, { timeout: 300000 })); }
    catch (e) { toast.error(e.message); } finally { setFixing(false); }
  };
  const run = async () => {
    if (text.trim().length < 80) return toast.error('Pega el escrito completo (mínimo 80 caracteres)');
    setBusy(true); setFixed(null);
    try { setData(await runAiJob(api, `/cerebro/matters/${matter.id}/review`, { text }, { timeout: 300000 })); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const v = VERDICT[data?.veredicto] || VERDICT.corregir;
  return (
    <div className="space-y-3 pt-3" data-testid="review-panel">
      <textarea data-testid="review-text-input" value={text} onChange={(e) => setText(e.target.value)} rows={7} placeholder="Pega aquí el escrito que vas a presentar..."
        className="w-full rounded-lg border border-slate-700 bg-slate-900 p-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-amber-500 outline-none" />
      <button data-testid="review-run-btn" onClick={run} disabled={busy} className="w-full rounded-lg bg-amber-500 py-2 text-sm font-semibold text-slate-950 hover:bg-amber-400 disabled:opacity-60 flex items-center justify-center gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileCheck2 className="h-4 w-4" />}{busy ? 'Revisando requisitos, plazos y citas oficiales...' : 'Revisar antes de presentar'}
      </button>
      {data && (
        <div className="space-y-3" data-testid="review-result">
          <div className="flex items-center justify-between rounded-xl bg-slate-900 p-3">
            <p data-testid="review-verdict" className={`text-sm font-semibold ${v[1]}`}>{v[0]}</p>
            <p data-testid="review-score" className="text-2xl font-bold">{data.puntaje}<span className="text-xs text-slate-500">/100</span></p>
          </div>
          {data.problemas?.map((p, i) => (
            <div key={i} data-testid={`review-issue-${i}`} className="rounded-lg border border-slate-800 p-2.5">
              <p className="text-xs text-white flex items-center gap-2"><Chip v={p.gravedad} /><span className="uppercase text-[10px] text-slate-500">{p.tipo}</span>{p.verificado && <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" title="Verificado con fuente oficial" />}</p>
              <p className="text-xs text-slate-300 mt-1">{p.detalle}</p>
              {p.sugerencia && <p className="text-xs text-emerald-300 mt-0.5">→ {p.sugerencia}</p>}
            </div>
          ))}
          {data.citas?.length > 0 && <div className="rounded-xl border border-slate-800 p-3" data-testid="review-citations"><p className="text-xs font-semibold text-sky-300 mb-1">Citas comprobadas contra el texto oficial</p>
            {data.citas.map((c, i) => <p key={i} className="text-xs text-slate-300">• {c.cita}: <span className={STATE[c.estado]}>{c.estado.replace('_', ' ')}</span></p>)}</div>}
          {data.mejoras_clave?.length > 0 && <div className="text-xs text-slate-300">{data.mejoras_clave.map((m, i) => <p key={i}>✓ {m}</p>)}</div>}
          {!fixed && (
            <button data-testid="review-rewrite-btn" onClick={rewrite} disabled={fixing} className="w-full rounded-lg bg-emerald-500 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:opacity-60 flex items-center justify-center gap-2">
              {fixing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}{fixing ? 'Corrigiendo cada problema (≈1-2 min)...' : 'Corregir el escrito automáticamente'}
            </button>
          )}
          {fixed && <RewriteResult data={fixed} title={matter.title} />}
        </div>
      )}
    </div>
  );
};
